// S9 Hiding rules (module-11 4a; REQ-10.2, REQ-10.3, REQ-10.7, REQ-10.9), top to bottom: what's hidden automatically
// on every app (CT-028: masking is automatic, so no app needs a rule of its own), "Found on your pages" (where the
// widget hid something, searched and paged by the server; with app.manage_masking an owner allows or hides a find and
// renames it), the owners' own rules (a page element's CSS selector, a library pattern, a custom pattern or an SDK
// context key; none required) and test captures on each address. No test mode and no masking check (CT-027).

import { MASKING_FIND_DESCRIPTION_MAX } from '@feedback/shared/constants/masking-finds';
import type {
  AppView,
  CreateMaskingRuleInput,
  MaskingFindView,
  UpdateMaskingFindInput,
} from '@feedback/shared/contracts/apps';
import { MASKING_FIND_LIMITS } from '@feedback/shared/masking/limits';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ErrorState } from '@/components/ErrorState';
import { RelativeTime } from '@/components/RelativeTime';
import { api, seg } from '@/lib/api';
import { errorText, fieldError, fieldErrors } from '../../lib/admin-api';
import { Badge, Button, Field, Input, Loading, Modal, Notice, Panel, Switch } from '../../ui/primitives';
import { appPath, installQuery, useAppWrite } from '../app-data';
import { HidingRuleEditor, KIND_LABEL } from '../HidingRuleEditor';
import {
  allowedByText,
  FIND_SEARCH_DEBOUNCE_MS,
  findPath,
  findSavedText,
  maskingFindsKey,
  maskingFindsQuery,
  pageRangeText,
} from '../masking-finds';

/** What the widget hides on every app, whatever its rules (C6, module-7 7a). */
const HIDDEN_AUTOMATICALLY = [
  'Everything typed into form boxes',
  'Email addresses and Australian phone numbers',
  'Card, tax file and Medicare numbers',
  'BSBs',
  'Table columns and fields labelled as personal, such as Name, Address, BSB or Account',
] as const;

const NO_FINDS_YET =
  'Nothing found yet. Places the widget hides appear here after the first report from this app’s pages.';

type Rule = AppView['masking']['rules'][number];

function ruleText(rule: Rule): string {
  return rule.pattern ? `${rule.pattern.name} (library)` : (rule.target ?? '');
}

function RuleRow({ app, rule }: { app: AppView; rule: Rule }) {
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState(rule.target ?? '');
  const [description, setDescription] = useState(rule.description ?? '');
  const path = appPath(app.id, `/masking-rules/${seg(rule.id)}`);
  const update = useAppWrite(
    app.id,
    (body: { target?: string; description: string | null }) => api.patch<AppView>(path, body),
    'Hiding rule saved',
  );
  const remove = useAppWrite(app.id, () => api.delete<AppView>(path), 'Hiding rule removed');
  const errors = fieldErrors(update.error);
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3 text-sm">
      <Badge>{KIND_LABEL[rule.kind]}</Badge>
      <span className="min-w-0 flex-1 font-mono break-all">{ruleText(rule)}</span>
      {rule.description ? <span className="w-full text-muted sm:w-auto">{rule.description}</span> : null}
      {app.can.manageMasking ? (
        <span className="flex gap-1">
          <Button size="sm" variant="ghost" aria-label={`Edit rule ${ruleText(rule)}`} onClick={() => setEditing(true)}>
            <Pencil aria-hidden="true" className="size-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Remove rule ${ruleText(rule)}`}
            busy={remove.isPending}
            onClick={() => remove.mutate(undefined)}
          >
            <Trash2 aria-hidden="true" className="size-4" />
          </Button>
        </span>
      ) : null}
      {remove.isError ? <p className="w-full text-xs text-danger">{errorText(remove.error)}</p> : null}
      <Modal
        open={editing}
        onOpenChange={setEditing}
        title="Edit hiding rule"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={update.isPending}
              onClick={() =>
                update.mutate(
                  {
                    ...(rule.pattern ? {} : { target: target.trim() }),
                    description: description.trim() || null,
                  },
                  { onSuccess: () => setEditing(false) },
                )
              }
            >
              Save rule
            </Button>
          </>
        }
      >
        {rule.pattern ? null : (
          <Field label={KIND_LABEL[rule.kind]} error={fieldError(errors, 'target')}>
            {({ id, invalid }) => (
              <Input id={id} aria-invalid={invalid} value={target} onChange={(e) => setTarget(e.target.value)} />
            )}
          </Field>
        )}
        <Field label="Description (optional)" error={fieldError(errors, 'description')}>
          {({ id }) => <Input id={id} value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        {update.isError && Object.keys(errors).length === 0 ? (
          <Notice tone="danger">{errorText(update.error)}</Notice>
        ) : null}
      </Modal>
    </li>
  );
}

function AddRule({ app }: { app: AppView }) {
  const create = useAppWrite(
    app.id,
    (body: CreateMaskingRuleInput) => api.post<AppView>(appPath(app.id, '/masking-rules'), body),
    'Hiding rule added',
  );
  const errors = fieldErrors(create.error);
  return (
    <HidingRuleEditor
      className="mt-4 flex flex-col gap-3 border-t border-border pt-4"
      busy={create.isPending}
      errors={errors}
      problem={create.isError && Object.keys(errors).length === 0 ? errorText(create.error) : undefined}
      onAdd={(rule) =>
        create.mutateAsync(rule).then(
          () => true,
          () => false, // shown on the fields, or below them
        )
      }
    />
  );
}

/** A switch or a rename of one find: the list is refetched before the write counts as done, so the row never flickers. */
function useFindWrite(appId: string, findId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMaskingFindInput) => api.patch<MaskingFindView>(findPath(appId, findId), body),
    // Errors show on the row (not as the shell's toast).
    meta: { silent: true },
    onSuccess: async (_find, body) => {
      await queryClient.invalidateQueries({ queryKey: maskingFindsKey(appId) });
      toast.success(findSavedText(body));
    },
  });
}

function FindRow({ app, find }: { app: AppView; find: MaskingFindView }) {
  const toggle = useFindWrite(app.id, find.id);
  const rename = useFindWrite(app.id, find.id);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(find.description);
  // While a switch is saving, it shows where it's going.
  const hidden = toggle.isPending && toggle.variables ? toggle.variables.state === 'hidden' : find.state === 'hidden';
  const renameErrors = fieldErrors(rename.error);
  const renameFieldError = fieldError(renameErrors, 'description');

  function saveDescription(description: string | null) {
    rename.mutate({ description }, { onSuccess: () => setRenaming(false) });
  }

  function submitRename() {
    const text = draft.trim();
    if (text === find.description) {
      setRenaming(false);
      return;
    }
    saveDescription(text);
  }

  return (
    <li data-testid="masking-find" className="flex flex-col gap-2 rounded-md border border-border p-3 text-sm">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="flex flex-wrap items-center gap-2 font-medium break-words">
            <span>{find.description}</span>
            {find.isNew ? <Badge tone="info">New</Badge> : null}
          </p>
          <code className="font-mono text-xs break-all">{find.selector}</code>
          <p className="text-xs text-muted">
            Page <span className="font-mono break-all">{find.pagePath}</span> · Last seen{' '}
            <RelativeTime iso={find.lastSeenAt} />
          </p>
        </div>
        {app.can.manageMasking ? (
          <div className="flex items-center gap-2">
            {renaming ? null : (
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Rename ${find.description}`}
                onClick={() => {
                  setDraft(find.description);
                  rename.reset();
                  setRenaming(true);
                }}
              >
                <Pencil aria-hidden="true" className="size-4" /> Rename
              </Button>
            )}
            <Switch
              testId="masking-find-toggle"
              label={`Hide ${find.description}`}
              checked={hidden}
              disabled={toggle.isPending}
              onCheckedChange={(on) => toggle.mutate({ state: on ? 'hidden' : 'allowed' })}
            />
            <span aria-hidden="true" className="w-14 text-xs text-muted">
              {hidden ? 'Hidden' : 'Allowed'}
            </span>
          </div>
        ) : (
          <Badge tone={hidden ? 'neutral' : 'warning'}>{hidden ? 'Hidden' : 'Allowed'}</Badge>
        )}
      </div>
      {find.state === 'allowed' ? <p className="text-xs text-muted">{allowedByText(find)}</p> : null}
      {toggle.isError ? (
        <p className="text-xs font-medium text-danger" role="alert">
          {errorText(toggle.error)}
        </p>
      ) : null}
      {renaming ? (
        <form
          className="flex flex-col gap-2 border-t border-border pt-2"
          onSubmit={(e) => {
            e.preventDefault();
            submitRename();
          }}
        >
          <Field
            label="New description"
            hint={`Up to ${MASKING_FIND_DESCRIPTION_MAX} characters.`}
            error={renameFieldError}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={draft}
                maxLength={MASKING_FIND_DESCRIPTION_MAX}
                onChange={(e) => setDraft(e.target.value)}
              />
            )}
          </Field>
          {rename.isError && !renameFieldError ? (
            <p className="text-xs font-medium text-danger" role="alert">
              {errorText(rename.error)}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" variant="primary" busy={rename.isPending} disabled={!draft.trim()}>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRenaming(false)}>
              Cancel
            </Button>
            {find.customDescription === null ? null : (
              <Button size="sm" variant="ghost" disabled={rename.isPending} onClick={() => saveDescription(null)}>
                Use the original
              </Button>
            )}
          </div>
        </form>
      ) : null}
    </li>
  );
}

function FoundOnYourPages({ app }: { app: AppView }) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState({ q: '', page: 1 });
  // The search waits for a pause in typing; a new search starts on its first page.
  useEffect(() => {
    const q = search.trim();
    const timer = window.setTimeout(
      () => setQuery((current) => (current.q === q ? current : { q, page: 1 })),
      FIND_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [search]);
  const finds = useQuery(maskingFindsQuery(app.id, query.q, query.page));
  const list = finds.data;
  const pages = list ? Math.max(1, Math.ceil(list.total / list.pageSize)) : 1;
  const goTo = (page: number) => setQuery((current) => ({ ...current, page }));

  return (
    <Panel
      title="Found on your pages"
      description="Each place where the widget hid something. It stays hidden unless someone allows it."
    >
      <div className="flex flex-col gap-3" data-testid="masking-finds">
        <Field label="Search finds">
          {({ id }) => (
            <Input
              id={id}
              type="search"
              data-testid="masking-find-search"
              placeholder="A description, technical name or page"
              maxLength={MASKING_FIND_LIMITS.placeChars}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
        </Field>
        {finds.isError ? (
          <ErrorState error={finds.error} onRetry={() => void finds.refetch()} />
        ) : !list ? (
          <Loading label="Loading finds" />
        ) : list.total === 0 ? (
          <p className="text-sm text-muted">{query.q ? `No finds match “${query.q}”.` : NO_FINDS_YET}</p>
        ) : (
          <>
            <ul className="flex flex-col gap-2" aria-busy={finds.isPlaceholderData || undefined}>
              {list.items.map((find) => (
                <FindRow key={find.id} app={app} find={find} />
              ))}
            </ul>
            <nav
              aria-label="Pages of finds"
              className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted"
            >
              <span>{pageRangeText(list.page, list.pageSize, list.total)}</span>
              <span className="flex gap-1">
                <Button size="sm" disabled={list.page <= 1} onClick={() => goTo(list.page - 1)}>
                  Previous
                </Button>
                <Button size="sm" disabled={list.page >= pages} onClick={() => goTo(list.page + 1)}>
                  Next
                </Button>
              </span>
            </nav>
          </>
        )}
      </div>
    </Panel>
  );
}

export function MaskingSection({ app }: { app: AppView }) {
  const install = useQuery(installQuery(app.id));

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Hiding rules"
        description="Personal details are hidden in screenshots and logs before a report is sent."
      >
        <Notice title="Hidden automatically on every app">
          <ul className="list-disc pl-5">
            {HIDDEN_AUTOMATICALLY.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="mt-1">Logs, network requests and page addresses are always masked.</p>
        </Notice>
      </Panel>

      <FoundOnYourPages app={app} />

      <Panel
        title="Your own rules"
        description="For anything the automatic hiding can’t recognise, such as a name in a sentence."
      >
        <ul className="flex flex-col gap-2">
          {app.masking.rules.length === 0 ? <li className="text-sm text-muted">No rules of your own yet.</li> : null}
          {app.masking.rules.map((rule) => (
            <RuleRow key={`${rule.id}:${rule.updatedAt}`} app={app} rule={rule} />
          ))}
        </ul>
        {app.can.manageMasking ? <AddRule app={app} /> : null}
      </Panel>

      <Panel
        title="Make a test capture"
        description="Opens the address with the drawer in test-capture mode, so you can see what a screenshot would show. Nothing is sent."
      >
        <ul className="flex flex-col gap-1">
          {install.isPending ? <li className="text-sm text-muted">Loading addresses…</li> : null}
          {install.data?.testCaptureLinks.length === 0 ? (
            <li className="text-sm text-muted">Add an address first.</li>
          ) : null}
          {install.data?.testCaptureLinks.map((link) => (
            <li key={link.url}>
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm underline"
              >
                Make a test capture: {link.environment} <span className="font-mono text-xs break-all">{link.url}</span>
                <ExternalLink aria-hidden="true" className="size-3.5" />
              </a>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
